/**
 * Тема оформления в трёх состояниях (раздел 10 архитектуры).
 *
 * Настройка устройства, а не учётной записи: она про экран, на который человек
 * смотрит, и синхронизировать её между телефоном и ноутбуком незачем — там
 * разный свет. Поэтому `localStorage`, а не мутация.
 *
 * «Как в системе» — не то же самое, что «светлая»: телефон сам переключается
 * по расписанию, и приложение обязано ехать вместе с ним. Поэтому у системного
 * состояния атрибута нет вовсе, и работает медиазапрос
 * `prefers-color-scheme`; явный выбор его перебивает (см. `styles.css`).
 */

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'nedelnik.theme';

export const THEME_LABELS: Record<Theme, string> = {
  light: 'Светлая',
  dark: 'Тёмная',
  system: 'Как в системе',
};

/**
 * Чтение и запись под `try/catch` по той же причине, что и у формата показа: в
 * приватном окне обращение к `localStorage` бросает исключение, и приложение
 * падало бы на выборе темы.
 */
export function readTheme(): Theme {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writeTheme(theme: Theme): void {
  try {
    if (theme === 'system') globalThis.localStorage?.removeItem(STORAGE_KEY);
    else globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  } catch {
    // Выбор не переживёт перезагрузку — это всё, что здесь ломается.
  }
}

/**
 * Ставит атрибут на корень документа. Для «как в системе» атрибут снимается:
 * его отсутствие и есть то состояние, в котором решает медиазапрос.
 */
export function applyTheme(theme: Theme): void {
  const root = globalThis.document?.documentElement;
  if (root === undefined) return;

  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  paintThemeColor();
  followSystemScheme();
}

/**
 * Полоса состояния и заголовок в списке задач Android берутся из
 * `theme-color`, и они обязаны быть цвета страницы. Цвет читается из `--bg`, а
 * не пишется здесь второй раз: иначе после правки палитры полоса разъедется со
 * страницей, и заметят это только на телефоне. Статичное значение в
 * `index.html` — для первого кадра и на случай, если стили ещё не применились.
 */
function paintThemeColor(): void {
  const document = globalThis.document;
  const meta = document?.querySelector('meta[name="theme-color"]');
  if (document === undefined || meta === null || meta === undefined) return;

  const background = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (background.length > 0) meta.setAttribute('content', background);
}

let followingSystem = false;

/**
 * «Как в системе» меняет палитру без участия приложения — телефон
 * переключается по расписанию, — и полосу состояния надо перекрасить вслед.
 * Подписка одна на всё время жизни страницы: при явной теме `--bg` от системы
 * не зависит, и лишняя перекраска ничего не меняет.
 */
function followSystemScheme(): void {
  if (followingSystem) return;
  const query = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
  if (query === undefined) return;

  query.addEventListener('change', paintThemeColor);
  followingSystem = true;
}

export function setTheme(theme: Theme): void {
  writeTheme(theme);
  applyTheme(theme);
}
